import { Suspense, lazy, useEffect, useRef, useState } from "react";

const NeuralCanvas = lazy(() => import("./NeuralCanvas"));

const ECO = ["AI", "DATA", "CLOUD", "AUTOMATION", "ANALYTICS", "SECURITY"];

function Fallback() {
  // lightweight static glow for mobile / reduced-motion / no-webgl
  return (
    <div className="absolute inset-0">
      <div
        className="absolute right-[10%] top-1/2 h-72 w-72 -translate-y-1/2 rounded-full opacity-70 lg:h-[26rem] lg:w-[26rem]"
        style={{
          background:
            "radial-gradient(circle, rgba(34,211,238,0.22), rgba(37,99,235,0.12) 45%, transparent 70%)",
        }}
      />
    </div>
  );
}

/**
 * Premium 3D neural-mind visual. Lazy-loaded and only mounted on capable devices.
 * Falls back to a static glow on mobile, low-power devices, no-WebGL, or reduced-motion.
 */
export default function NeuralHero({ className = "" }) {
  const [enabled, setEnabled] = useState(false);
  const labelRefs = useRef([]);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const weak = window.innerWidth < 768 || (navigator.hardwareConcurrency || 8) < 4;
    let webgl = true;
    try {
      const c = document.createElement("canvas");
      webgl = !!(c.getContext("webgl2") || c.getContext("webgl"));
    } catch {
      webgl = false;
    }
    if (!reduce && !weak && webgl) setEnabled(true);
  }, []);

  return (
    <div className={`pointer-events-none absolute inset-0 ${className}`} aria-hidden="true">
      {enabled ? (
        <Suspense fallback={<Fallback />}>
          <NeuralCanvas labelRefs={labelRefs} />
        </Suspense>
      ) : (
        <Fallback />
      )}

      {enabled && (
        <div className="absolute inset-0">
          {ECO.map((l, i) => (
            <span
              key={l}
              ref={(el) => (labelRefs.current[i] = el)}
              className="absolute left-0 top-0 whitespace-nowrap rounded-full border border-cyan-300/25 bg-white/[0.06] px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-cyan-100/90 backdrop-blur-sm transition-opacity duration-700 will-change-transform"
              style={{ opacity: 0 }}
            >
              {l}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
